/**
 * Billing: the dashboard's third screen.
 *
 * The checkout tells a buyer "You can turn renewal off at any time in your
 * dashboard under Billing". Thomas, Sep 9 2026: build Billing and make that
 * statement true. So this screen carries the plan, the renewal control, the
 * invoices and the documents, on both hosts, and the Worker carries the
 * endpoint the control binds to (PATCH /api/enterprise/billing).
 *
 * ── THE RENEWAL CONTROL ───────────────────────────────────────────────────
 * One control, two states, and it never hides its consequence: turning
 * renewal off says on the screen when the dashboard goes export-only and
 * that no student is affected. On a live tenant (Sep 10 2026) the control
 * is the Worker's route: owner only, Stripe told first when it is
 * configured, audited, and every administrator emailed when mail is
 * configured; the screen says which of those happened. On /demo the control
 * changes the view and the sample banner says it changes nothing real.
 *
 * ── WHAT IT DOES NOT PRETEND ──────────────────────────────────────────────
 * A band is not changed here. Moving to a larger band shows a prorated
 * amount before anything is charged (Terms 4.3), which is a conversation
 * with a number in it, so the screen says how to start it and does not ship
 * a button that would charge a university without one. A viewer cannot open
 * billing at all (the route is for owners and administrators), and the
 * screen says so rather than rendering an empty plan.
 */

import { useMemo, useState } from "react";

import {
  AdminShell,
  ExportPdfButton,
  type PaletteCommand,
} from "~/app/components/AdminShell";
import { LiveGate, useLiveTenant } from "~/app/components/LiveTenant";
import { viewerOf, type SessionPayload } from "~/app/lib/session";
import {
  Button,
  Card,
  CardBody,
  CardFootnote,
  CardHeader,
  CardTitle,
  CellValue,
  Chip,
  DataTable,
  SectionHead,
  StateCard,
} from "~/app/components/primitives";
import { ADMIN_SECTIONS } from "~/app/lib/admin-nav";
import { patchBilling, type BillingPayload } from "~/app/lib/api";
import {
  DEFAULT_FILTERS,
  deriveCohort,
  scenarioFromSearch,
} from "~/app/lib/demo-data";
import { formatCount, formatDate, formatDateRange, formatMoney } from "~/app/lib/format";
import { currentVersion } from "~/app/lib/legal";
import { liveView } from "~/app/lib/live-view";
import {
  CANCELLATION_NOTICE_DAYS,
  EXPORT_RETENTION_MONTHS,
  INVOICE_NET_DAYS,
  LEGAL,
  TERM_MONTHS,
  bandById,
  bandFromWire,
} from "~/app/lib/pricing";

interface InvoiceRow {
  readonly id: string;
  readonly date: string;
  readonly description: string;
  readonly amount: number;
  readonly status: "Paid" | "Open";
}

const DAY_MS = 86_400_000;

/** The plan's rows, from the Worker's billing payload or the sample. */
interface PlanRows {
  readonly bandName: string;
  /** The money alone, for the head: "$7,000". */
  readonly amount: string;
  /** What that money buys, beside it: "a year". */
  readonly cadence: string;
  readonly billing: string;
  readonly period: string;
  readonly limit: number;
}

/** "$7,000 a year", for a sentence. The head shows the two parts apart. */
const priceLine = (rows: PlanRows): string => (rows.cadence ? `${rows.amount} ${rows.cadence}` : rows.amount);

function livePlanRows(plan: NonNullable<BillingPayload["plan"]>, interval: string): PlanRows {
  const band = bandFromWire(plan.band);
  const amount = formatMoney(plan.amountCents / 100);
  return {
    bandName: band?.name ?? plan.bandLabel ?? plan.band,
    amount,
    cadence: plan.interval === "year" ? "a year" : "a month",
    billing:
      plan.collectionMethod === "send_invoice"
        ? `${interval}, by invoice, net ${INVOICE_NET_DAYS} days`
        : `${interval}, by card`,
    period:
      plan.periodStart !== null && plan.renewsAt !== null
        ? formatDateRange(plan.periodStart * 1000, plan.renewsAt * 1000)
        : "Not on file",
    limit: plan.bandLimit ?? band?.capacity ?? 0,
  };
}

export function AdminBilling({
  basePath,
  demo = false,
  session,
}: {
  basePath: "/admin" | "/demo";
  demo?: boolean;
  /** The signed-in person, on /admin. Their plan replaces the sample's. */
  session?: SessionPayload;
}) {
  const live = useLiveTenant(session);
  const scenario = useMemo(
    () => scenarioFromSearch(typeof window === "undefined" ? "" : window.location.search),
    [],
  );
  const sampleView = useMemo(() => deriveCohort(scenario, DEFAULT_FILTERS), [scenario]);
  const data = live.state.kind === "ready" ? live.state.data : null;
  const view = useMemo(() => (data ? liveView(data) : sampleView), [data, sampleView]);

  const [sampleRenews, setSampleRenews] = useState(true);
  /** Set after a successful PATCH; until then the payload's own flag. */
  const [liveRenews, setLiveRenews] = useState<boolean | null>(null);
  const [liveEndsAt, setLiveEndsAt] = useState<number | null>(null);
  const [changedAt, setChangedAt] = useState<Date | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  if (session && live.state.kind !== "ready") {
    return <LiveGate state={live.state} reload={live.reload} />;
  }

  const { institution, seat } = view;
  const isLive = data !== null;
  const billing = data?.billing ?? null;
  const plan = billing?.plan ?? null;
  const owner = !isLive || session?.role === "owner";
  const band = bandById(institution.capacityBand);
  const annual = institution.planInterval.toLowerCase() === "annual";

  const rows: PlanRows | null = isLive
    ? plan
      ? livePlanRows(plan, institution.planInterval)
      : null
    : {
        bandName: band.name,
        amount:
          annual && band.annual !== null
            ? formatMoney(band.annual)
            : band.monthly !== null
              ? formatMoney(band.monthly)
              : "Quoted",
        cadence: annual && band.annual !== null ? "a year" : band.monthly !== null ? "a month" : "",
        billing: `${institution.planInterval}, by invoice, net ${INVOICE_NET_DAYS} days`,
        period: formatDateRange(institution.subscribedAt, institution.renewsAt),
        limit: seat.purchased,
      };

  const renews = isLive ? (liveRenews ?? (plan ? !plan.cancelAtPeriodEnd : true)) : sampleRenews;

  const toggle = async () => {
    if (!isLive) {
      setSampleRenews((on) => !on);
      setChangedAt(new Date());
      return;
    }
    setBusy(true);
    setNotice(null);
    const result = await patchBilling(!renews);
    setBusy(false);
    if (result.ok) {
      setLiveRenews(result.value.renew);
      setLiveEndsAt(result.value.endsAt ?? null);
      setChangedAt(new Date());
      setNotice(
        result.value.notified
          ? "Your administrators have been emailed."
          : "Saved. Email notices are not configured on this deployment yet.",
      );
    } else {
      setNotice(result.error.message);
    }
  };

  const renewsAt = new Date(institution.renewsAt);
  const deadline = new Date(renewsAt.getTime() - CANCELLATION_NOTICE_DAYS * DAY_MS);
  /* Monthly plans are month to month (Sep 15 2026): renewal off bills one more
     full month, so the day the dashboard stays live until is the Worker's
     `endsAt`, not the next charge. Annual plans end with their period. */
  const endsAtSeconds = liveEndsAt ?? plan?.endsAt ?? null;
  const endsOn = endsAtSeconds !== null ? new Date(endsAtSeconds * 1000).toISOString() : institution.renewsAt;

  const sampleInvoices: readonly InvoiceRow[] = [
    {
      id: "inv-1",
      date: institution.subscribedAt,
      description: `${band.name}, ${annual ? "annual" : "monthly"} subscription`,
      amount: annual ? (band.annual ?? 0) : (band.monthly ?? 0),
      status: "Paid",
    },
  ];

  const terms = currentVersion("enterprise-terms");
  const addendum = currentVersion("data-sharing-addendum");
  const documents = {
    terms: billing?.documents.terms ?? terms.path,
    addendum: billing?.documents.dataSharingAddendum ?? addendum.path,
    subprocessors: billing?.documents.subprocessors ?? "/legal/subprocessors",
    w9: billing?.documents.w9 ?? LEGAL.w9Url,
  };

  const commands: PaletteCommand[] = [
    ...(owner && (!isLive || plan)
      ? [
          {
            id: "toggle-renewal",
            group: "Billing",
            label: renews ? "Turn off renewal" : "Turn renewal back on",
            hint: `Renews ${formatDate(institution.renewsAt)}`,
            keywords: "renew cancel subscription auto",
            run: () => void toggle(),
          },
        ]
      : []),
    {
      id: "print",
      group: "Share",
      label: "Export as PDF, or print",
      keywords: "board pdf paper export print",
      run: () => window.print(),
    },
  ];

  return (
    <AdminShell
      institution={institution}
      viewer={viewerOf(session)}
      basePath={basePath}
      page="billing"
      isSample={!isLive}
      sections={[...ADMIN_SECTIONS]}
      seat={seat}
      headerActions={<ExportPdfButton />}
      commands={commands}
    >
      <section className="e-sec" style={{ marginTop: 0 }}>
        <SectionHead
          level="page"
          id="billing"
          title="Billing"
          lede="Your plan, your renewal, and your invoices."
        />


        {isLive && billing === null ? (
          /* A viewer. The Worker's billing route is for owners and
             administrators, and the 403 is the designed answer. */
          <div className="e-grid">
            <Card span="full">
              <CardBody>
                <StateCard variant="empty" title="Billing is available to owners and administrators">
                  Your access is {institution.viewerRole}. An owner or an administrator on
                  your team can open this screen, and Team and roles names them.
                </StateCard>
              </CardBody>
            </Card>
          </div>
        ) : (
          <>
            <div className="e-grid">
              <Card span={2}>
                <CardHeader>
                  <CardTitle>Your plan</CardTitle>
                </CardHeader>
                <CardBody>
                  {rows ? (
                    <>
                      {/* What the institution pays, and how much of it is in
                          use, before the terms of it (Thomas, Sep 17 2026).
                          The price and the band were two rows among five, at
                          the same weight as the net-30 line; they are the two
                          things an owner opens this screen for. The rows that
                          remain are the terms, which is what a row is for. */}
                      <div className="e-plan-head">
                        <p className="e-plan-price">
                          <span className="e-figure-lead">{rows.amount}</span>
                          {rows.cadence ? <span className="e-plan-cadence">{rows.cadence}</span> : null}
                        </p>
                        <p className="e-plan-band">{rows.bandName}</p>
                        <div className="e-plan-seats">
                          <p className="e-plan-seats-line">
                            <CellValue cell={seat.claimed} format={formatCount} label="Students claimed" />
                            <span className="e-defs-of"> of {formatCount(rows.limit)} claimed</span>
                          </p>
                          {seat.claimed.kind === "value" ? (
                            <div
                              className="e-plan-track"
                              role="progressbar"
                              aria-valuemin={0}
                              aria-valuemax={rows.limit}
                              aria-valuenow={seat.claimedRaw}
                              aria-label={`${formatCount(seat.claimedRaw)} students claimed of a ${formatCount(rows.limit)} student band`}
                            >
                              <i style={{ width: `${Math.max(Math.round(seat.fraction * 100), seat.claimedRaw > 0 ? 2 : 0)}%` }} />
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <dl className="e-defs">
                        <div>
                          <dt>Billing</dt>
                          <dd><span className="e-ui">{rows.billing}</span></dd>
                        </div>
                        <div>
                          <dt>Current period</dt>
                          <dd><span className="e-ui">{rows.period}</span></dd>
                        </div>
                      </dl>
                    </>
                  ) : (
                    <StateCard variant="empty" title="No plan on file">
                      P3 has not recorded a subscription for your institution yet. Email{" "}
                      {LEGAL.contactEmail} and we will put it right.
                    </StateCard>
                  )}
                </CardBody>
                <CardFootnote>
                  <p className="e-note">
                    {billing?.capacity.note ??
                      `To change your band, email ${LEGAL.contactEmail}. Nothing is charged when more students claim you.`}
                  </p>
                </CardFootnote>
              </Card>

              <Card solo>
                <CardHeader aside={<Chip tone={renews ? "accent" : "neutral"}>{renews ? "On" : "Off"}</Chip>}>
                  <CardTitle>Automatic renewal</CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="e-vstack">
                    {renews ? (
                      <>
                        {annual ? (
                          <>
                            {/* The ui step, the same as the plan's values beside it
                                (Sep 17 2026): a fact, not prose. Notes stay notes. */}
                            <p className="e-ui">
                              <strong>Renews on {formatDate(institution.renewsAt)}</strong> for a
                              further {TERM_MONTHS} months{rows ? ` at ${priceLine(rows)}` : ""}.
                            </p>
                            <p className="e-note">
                              Changes until {formatDate(deadline.toISOString())}, {CANCELLATION_NOTICE_DAYS} days
                              before renewal. Administrators are emailed.
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="e-ui">
                              <strong>Renews each month</strong>{rows ? ` at ${priceLine(rows)}` : ""}. Next charge{" "}
                              {formatDate(institution.renewsAt)}.
                            </p>
                            <p className="e-note">
                              Turn renewal off and your subscription ends at the end of the next full month.
                              Administrators are emailed.
                            </p>
                          </>
                        )}
                        {owner && (!isLive || plan) ? (
                          <Button variant="secondary" size="sm" onClick={() => void toggle()} disabled={busy}>
                            Turn off renewal
                          </Button>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <p className="e-ui">
                          <strong>Renewal is off.</strong> Your dashboard stays live until{" "}
                          {formatDate(endsOn)}, then becomes export-only for{" "}
                          {EXPORT_RETENTION_MONTHS} months.
                        </p>
                        <p className="e-note">
                          {changedAt ? `Changed just now by ${institution.contactName}. ` : null}
                          {demo ? "In the sample nothing is sent." : null}
                        </p>
                        {owner && (!isLive || plan) ? (
                          <Button variant="primary" size="sm" onClick={() => void toggle()} disabled={busy}>
                            Turn renewal back on
                          </Button>
                        ) : null}
                      </>
                    )}
                    {isLive && !owner ? (
                      <p className="e-note">An owner on your team can change this.</p>
                    ) : null}
                    {notice ? (
                      <p className="e-note" role="status">
                        {notice}
                      </p>
                    ) : null}
                  </div>
                </CardBody>
                <CardFootnote>
                  <p className="e-note">Your students are never affected.</p>
                </CardFootnote>
              </Card>
            </div>

            <div className="e-grid">
              <Card variant="table" span={2}>
                <CardHeader>
                  <CardTitle>Invoices</CardTitle>
                </CardHeader>
                <CardBody>
                  {isLive ? (
                    billing && billing.invoices.state === "ok" && billing.invoices.rows.length > 0 ? (
                      <p className="e-ui">
                        {formatCount(billing.invoices.rows.length)} on file. Ask {LEGAL.contactEmail} for a copy.
                      </p>
                    ) : (
                      <StateCard
                        variant="empty"
                        title={
                          billing && billing.invoices.state === "unavailable"
                            ? "Invoice history is not available here yet"
                            : "No invoices yet"
                        }
                      >
                        {billing?.invoices.reason ??
                          "Your first invoice appears here once it is issued."}{" "}
                        For a copy of any invoice, email {LEGAL.contactEmail}.
                      </StateCard>
                    )
                  ) : (
                    <DataTable
                      caption="Invoices for this subscription"
                      rows={sampleInvoices}
                      rowKey={(row) => row.id}
                      cap={12}
                      moreNoun="invoice"
                      columns={[
                        {
                          key: "date",
                          header: "Date",
                          cell: (row) => formatDate(row.date),
                          text: (row) => formatDate(row.date),
                        },
                        {
                          key: "description",
                          header: "Description",
                          cell: (row) => row.description,
                          text: (row) => row.description,
                        },
                        {
                          key: "amount",
                          header: "Amount",
                          cell: (row) => <span className="e-num">{formatMoney(row.amount)}</span>,
                          text: (row) => formatMoney(row.amount),
                        },
                        {
                          key: "status",
                          header: "Status",
                          cell: (row) => row.status,
                          text: (row) => row.status,
                          secondary: true,
                        },
                      ]}
                    />
                  )}
                </CardBody>
                {/* Net terms and a purchase order are an invoice plan's; a card plan
                    is charged by Stripe (Sep 15 2026). */}
                {!isLive || plan?.collectionMethod === "send_invoice" ? (
                  <CardFootnote>
                    <p className="e-note">
                      Net {INVOICE_NET_DAYS}. Send a purchase order number before the invoice
                      is issued.
                    </p>
                  </CardFootnote>
                ) : null}
              </Card>

              <Card solo>
                <CardHeader>
                  <CardTitle>Documents</CardTitle>
                </CardHeader>
                <CardBody>
                  <ul className="e-linklist">
                    <li>
                      <a className="e-ui" href={documents.terms}>
                        {terms.shortTitle} {terms.version}
                      </a>
                    </li>
                    <li>
                      <a className="e-ui" href={documents.addendum}>
                        {addendum.shortTitle} {addendum.version}
                      </a>
                    </li>
                    <li>
                      <a className="e-ui" href={documents.subprocessors}>
                        Subprocessor list
                      </a>
                    </li>
                    <li>
                      {/* Published Sep 13 2026 (Security page, the
                          procurement pack); served by the Worker. */}
                      <a className="e-ui" href={documents.w9}>
                        W-9
                      </a>
                    </li>
                  </ul>
                </CardBody>
              </Card>
            </div>
          </>
        )}
      </section>
    </AdminShell>
  );
}
